import { useState } from 'react';
import { createPortal } from 'react-dom';
import { api } from '../api.ts';
import type { AuthUser } from '../hooks/useApp.ts';

interface Props {
  onClose: () => void;
  onSuccess: (user: AuthUser) => void;
}

export function AuthModal({ onClose, onSuccess }: Props): JSX.Element {
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const { user } = mode === 'login'
        ? await api.login(username, password)
        : await api.register(username, password);
      onSuccess(user);
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : 'something went wrong');
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div className="modal-backdrop" onClick={onClose}>
      <form className="modal" onClick={e => e.stopPropagation()} onSubmit={submit}>
        <h2>{mode === 'login' ? 'Welcome back' : 'Join the community'}</h2>
        <div className="sub">
          {mode === 'login'
            ? 'Sign in to comment, save reading progress, and join the discussion.'
            : 'Create an account in 10 seconds. Username and password — that\'s it.'}
        </div>

        <div className="field">
          <label>Username</label>
          <input
            value={username}
            onChange={e => setUsername(e.target.value)}
            placeholder="3-20 characters"
            autoFocus
            autoComplete={mode === 'login' ? 'username' : 'new-username'}
            required
          />
        </div>
        <div className="field">
          <label>Password</label>
          <input
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="at least 8 characters"
            autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
            required
            minLength={8}
          />
        </div>

        {err && <div className="err">{err}</div>}

        <div className="actions">
          <button type="submit" className="primary" disabled={busy}>
            {busy ? '…' : (mode === 'login' ? 'Sign in' : 'Create account')}
          </button>
          <span className="switch">
            {mode === 'login' ? (
              <>New here? <a onClick={() => setMode('register')}>Create an account</a></>
            ) : (
              <>Already have one? <a onClick={() => setMode('login')}>Sign in</a></>
            )}
          </span>
        </div>
      </form>
    </div>,
    document.body,
  );
}
